import React, { useMemo, useRef, useState } from "react";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import { IconButton, Menu, MenuItem, MenuList, Popover, Stack, Typography } from "@mui/material";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useUserRoleSelector } from "store";
import { IFileMetaDetail } from "store/types";

import { FileType, RoleName } from "../../../constants";
import { useFileHook } from "../Hooks/use-file-hook";

export type FileActionsHook = typeof useFileHook;

const menuPaperSx = {
  borderRadius: "8px",
  boxShadow:
    "0px 3px 3px -2px rgba(38,54,70,0.16), 0px 3px 4px 0px rgba(38,54,70,0.1), 0px 1px 8px 0px rgba(38,54,70,0.08)",
  minWidth: 200,
  py: "8px"
};

const menuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  px: "12px",
  py: "8px",
  fontSize: 16,
  color: "#071f35",
  minHeight: 40,
  "& .MuiSvgIcon-root": { fontSize: 20, color: "#496682", flexShrink: 0 },
  "&:hover": { backgroundColor: "#ecf1f8" }
};

const toolsSubMenuItems = [
  // { label: "Categorize", icon: <CategoryOutlinedIcon /> },
  { label: "Look Through", icon: <AccountTreeOutlinedIcon /> },
  { label: "Add Task", icon: <CheckOutlinedIcon /> }
];

interface ToolsSubmenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onItemClick: (label: string) => void;
  availableHandlers: Record<string, (() => void) | undefined>;
}

const ToolsSubmenu: React.FC<ToolsSubmenuProps> = ({
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onItemClick,
  availableHandlers
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "top", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "right" }}
    disableRestoreFocus
    PaperProps={{ sx: { ...menuPaperSx, mr: "4px" } }}
    sx={{ pointerEvents: "none" }}
    slotProps={{ paper: { sx: { ...menuPaperSx, mr: "4px", pointerEvents: "auto" } } }}
  >
    <MenuList disablePadding onMouseEnter={onMouseEnter} onMouseLeave={onClose}>
      {toolsSubMenuItems
        .filter(({ label }) => Boolean(availableHandlers[label]))
        .map(({ label, icon }) => (
          <MenuItem
            key={label}
            sx={menuItemSx}
            onClick={(e) => {
              e.stopPropagation();
              onItemClick(label);
              onClose();
            }}
          >
            {icon}
            <Typography sx={{ fontSize: 16, color: "#071f35", fontFamily: "Roboto, sans-serif" }}>{label}</Typography>
          </MenuItem>
        ))}
    </MenuList>
  </Popover>
);

export interface Props {
  row: IFileMetaDetail;
  refetchHandler: () => Promise<void>;
  useHook?: FileActionsHook;
}

const READ_ONLY_ROLES = [RoleName.Guest, RoleName.Viewer, RoleName.TeamViewer];

export const FileActions: React.FC<Props> = ({ row, refetchHandler, useHook = useFileHook }) => {
  const role = useUserRoleSelector();
  const isReadOnly = Boolean(role?.name && READ_ONLY_ROLES.includes(role.name as RoleName));

  const {
    handleShare,
    handleDelete,
    handleDownload,
    handleAddTask,
    // handleCategorize,
    handleDataExtraction,
    handleLookThrough,
    handleFileInfo,
    handleExtractK1
  } = useHook({
    file: {
      id: row.id,
      name: row.name,
      type: row.type,
      created_at: row.created_at || "",
      section_id: row.section_id || null,
      deal_id: row.deal?.id ?? null
    },
    refetchHandler
  });
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
  const toolsItemRef = useRef<HTMLLIElement | null>(null);
  const closeSubmenuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => {
    if (closeSubmenuTimerRef.current) {
      clearTimeout(closeSubmenuTimerRef.current);
      closeSubmenuTimerRef.current = null;
    }
    setMenuAnchor(null);
    setToolsAnchor(null);
  };

  const cancelCloseSubmenuTimer = () => {
    if (closeSubmenuTimerRef.current) {
      clearTimeout(closeSubmenuTimerRef.current);
      closeSubmenuTimerRef.current = null;
    }
  };

  const handleAction = (fn?: () => void) => {
    closeMenu();
    fn?.();
  };

  const onToolsHover = () => {
    cancelCloseSubmenuTimer();
    if (toolsItemRef.current) {
      setToolsAnchor(toolsItemRef.current);
    }
  };

  const onToolsLeave = () => {
    closeSubmenuTimerRef.current = setTimeout(() => {
      closeSubmenuTimerRef.current = null;
      setToolsAnchor(null);
    }, 150);
  };

  const hasTools = Boolean(handleDataExtraction || handleLookThrough || handleAddTask);

  const menuOptions = useMemo(
    () => [
      {
        title: "File Info",
        icon: InfoOutlinedIcon,
        handler: handleFileInfo,
        hidden: isReadOnly || !handleFileInfo || !row.deal?.id,
        disabled: !handleFileInfo,
        isSubmenu: false,
        quickAction: false
      },
      {
        title: "Share",
        icon: PersonAddOutlinedIcon,
        handler: handleShare,
        hidden: isReadOnly || !handleShare || !row.deal?.id || row.source === "Dataroom",
        disabled: !handleShare,
        isSubmenu: false,
        quickAction: false
      },
      {
        title: "Download",
        icon: FileDownloadOutlinedIcon,
        handler: handleDownload,
        hidden: !handleDownload,
        disabled: !handleDownload,
        isSubmenu: false,
        quickAction: true
      },
      {
        title: "Data Extraction",
        icon: ExtratableRepoIcon,
        iconSx: { width: 20, height: 16, fill: "#496682" },
        handler: handleDataExtraction,
        hidden: isReadOnly || !handleDataExtraction || !row.deal?.id,
        disabled: row.type !== FileType["PDF"] || !handleDataExtraction,
        isSubmenu: false,
        quickAction: true
      },
      {
        title: "Extract K1",
        icon: NoteAddOutlinedIcon,
        handler: handleExtractK1,
        hidden: isReadOnly || !handleExtractK1,
        disabled: row.type !== FileType["PDF"] || row.document_type !== "Tax" || !handleExtractK1,
        isSubmenu: false,
        quickAction: false
      },
      {
        title: "Tools",
        icon: HandymanOutlinedIcon,
        handler: undefined,
        hidden: isReadOnly || !hasTools || row.type !== FileType["PDF"],
        disabled: false,
        isSubmenu: true,
        quickAction: false
      },
      {
        title: "Delete",
        icon: DeleteOutlineOutlinedIcon,
        handler: handleDelete,
        hidden: isReadOnly || !handleDelete,
        disabled: !handleDelete,
        isSubmenu: false,
        quickAction: true
      }
    ],
    [
      row,
      isReadOnly,
      hasTools,
      handleFileInfo,
      handleShare,
      handleDownload,
      handleDataExtraction,
      handleExtractK1,
      handleDelete
    ]
  );

  const hasAvailableActions = useMemo(() => {
    return menuOptions.filter((option) => !option.hidden && !option.quickAction).length;
  }, [menuOptions]);

  if (!row) return null;

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.Guest, RoleName.Viewer, RoleName.TeamViewer]}
    >
      <>
        <Stack
          onClick={(e) => e.stopPropagation()}
          direction="row"
          className="row-hover-actions"
          justifyContent="flex-end"
          sx={{ opacity: 0, transition: "opacity 0.1s" }}
        >
          {menuOptions
            .filter((option) => !option.hidden && option.quickAction)
            .map((option) => (
              <MaterialTooltip key={option.title} title={option.title} isShow>
                <IconButton size="sm" sx={{ color: "#071f35" }} onClick={option.handler} disabled={option.disabled}>
                  <option.icon
                    sx={{
                      ...option.iconSx,
                      fill: "#071f35",
                      fontSize: "20px",
                      ...(option.title === "Data Extraction" && { width: 17, height: 13 }),
                      ...(option.disabled && { opacity: 0.5 })
                    }}
                  />
                </IconButton>
              </MaterialTooltip>
            ))}
          {Boolean(hasAvailableActions) && (
            <IconButton
              size="sm"
              onClick={openMenu}
              aria-expanded={Boolean(menuAnchor)}
              sx={{ p: "4px", color: "#071f35", ...(menuAnchor && { opacity: 0.7 }) }}
            >
              <MoreVertIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Stack>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: menuPaperSx } }}
          MenuListProps={{ sx: { padding: 0 } }}
        >
          <MenuList disablePadding>
            {menuOptions
              .filter((option) => !option.hidden)
              .map((option) =>
                option.isSubmenu ? (
                  <MenuItem
                    key={option.title}
                    ref={toolsItemRef}
                    sx={{ ...menuItemSx, ...(Boolean(toolsAnchor) && { backgroundColor: "#ecf1f8" }) }}
                    onMouseEnter={onToolsHover}
                    onMouseLeave={onToolsLeave}
                  >
                    <option.icon sx={{ fontSize: 20, ...option.iconSx }} />
                    <Typography sx={{ fontSize: 16, color: "#071f35", flex: 1 }}>{option.title}</Typography>
                    <KeyboardArrowRightIcon sx={{ fontSize: 20, color: "#496682", ml: "auto" }} />
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={option.title}
                    sx={menuItemSx}
                    onClick={() => handleAction(option.handler)}
                    disabled={option.disabled}
                  >
                    <option.icon sx={{ fontSize: 20, ...option.iconSx }} />
                    <Typography sx={{ fontSize: 16, color: "#071f35" }}>{option.title}</Typography>
                  </MenuItem>
                )
              )}
          </MenuList>
        </Menu>

        <ToolsSubmenu
          anchorEl={toolsAnchor}
          open={Boolean(toolsAnchor)}
          onClose={() => {
            cancelCloseSubmenuTimer();
            setToolsAnchor(null);
          }}
          onMouseEnter={cancelCloseSubmenuTimer}
          availableHandlers={{
            "Data Extraction": handleDataExtraction,
            "Look Through": handleLookThrough,
            "Add Task": handleAddTask
          }}
          onItemClick={(label) => {
            closeMenu();
            // if (label === "Data Extraction") handleDataExtraction?.();
            // else if (label === "Categorize") handleCategorize?.();
            if (label === "Look Through") handleLookThrough?.();
            else if (label === "Add Task") handleAddTask?.();
          }}
        />
      </>
    </RoleComponentGuard>
  );
};
